import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent,
  Button,
  useTheme,
  Paper,
  alpha,
  CardMedia,
  useMediaQuery,
  Fade,
  CircularProgress,
  Fab,
  Zoom,
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Security as SecurityIcon,
  Psychology as PsychologyIcon,
  ShowChart as ShowChartIcon,
  Analytics as AnalyticsIcon,
  Timeline as TimelineIcon,
  ArrowUpward as ArrowUpwardIcon,
  Lightbulb as LightbulbIcon,
  QueryStats as QueryStatsIcon,
  DataUsage as DataUsageIcon,
  Devices as DevicesIcon,
  Category as SectorIcon,
  AttachMoney as AttachMoneyIcon,
  Speed as SpeedIcon,
  RocketLaunch as RocketLaunchIcon,
  Lock as LockIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Info as InfoIcon,
  Menu as MenuIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Home as HomeIcon,
  FeaturedPlayList as FeaturedPlayListIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Build as BuildIcon,
  DirectionsRun as RunnerIcon,
  EmojiEvents as AchievementIcon,
  FamilyRestroom as FamilyIcon,
  Elderly as ElderlyIcon,
  School as SchoolIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import { investmentAPI } from '../../api/investmentAPI';
import { useLoadingStatus } from '../../contexts/LoadingStatusContext';
import { StockChart } from '../charts/StockChart';
import { getSignalColor, getTrendColor, baseColors, semanticColors } from '../../theme/themesV3';
import { SubscriptionPlans } from '../auth/SubscriptionPlans';

const UnregisteredContent: React.FC<{ 
  setShowRegistration: (show: boolean) => void,
  urlPrimaryBase: string,
  getBusinessDates: (startDate: Date, count: number) => string[]
}> = ({ setShowRegistration, urlPrimaryBase, getBusinessDates }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const [activeSection, setActiveSection] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [featureTab, setFeatureTab] = useState(0);
  const [expandedPricingFeatures, setExpandedPricingFeatures] = useState<number | false>(false);
  const [profileIndex, setProfileIndex] = useState(0);
  
  // Add missing state variables
  const [etfData, setEtfData] = useState<{[key: string]: any}>({});
  const [loading, setLoading] = useState(true);
  
  // Section references for navigation
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  
  // Add this state at the component level (near other useState declarations)
  const [activeProfileIndex, setActiveProfileIndex] = useState<number | null>(null);
  const [hoveredProfileIndex, setHoveredProfileIndex] = useState<number | null>(null);
  
  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  // Scroll to section function
  const scrollToSection = (index: number) => {
    if (sectionRefs.current[index]) {
      sectionRefs.current[index]?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  
  // Modified scroll handler to remove section navigator functionality
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
      
      // Update active section based on scroll position
      const currentPosition = window.scrollY + 100;
      
      for (let i = sectionRefs.current.length - 1; i >= 0; i--) {
        const section = sectionRefs.current[i];
        if (section) {
          if (section.offsetTop <= currentPosition) {
            if (activeSection !== i) {
              setActiveSection(i);
            }
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSection]);
  
  // Initialize API
  const { addStatus, removeStatus } = useLoadingStatus();
  const api = useMemo(() => investmentAPI(addStatus, removeStatus), []);
  
  // Mock data for stock visualization
  const stockData = [65, 59, 80, 81, 56, 55, 72, 60, 59, 80, 85, 90, 85, 92, 88, 95];
  
  // Generate historical price data for charts from free report if available
  const [historicalData, setHistoricalData] = useState<{[key: string]: number[]}>({
    SPY: stockData.map(val => val * (1 + (Math.random() * 0.1 - 0.05))),
    QQQ: stockData.map(val => val * (1 + (Math.random() * 0.1 - 0.05)))
  });
  console.log('ETF Data:', etfData);
  // Fetch real-time ETF data
  useEffect(() => {
    const fetchETFData = async () => {
      try {
        setLoading(true);
        console.log('Starting ETF data fetch...');
        
        // Clear any previous data while loading
        setEtfData({});
        
        const data = await api.getMultiTickerData(['SPY', 'QQQ'], null);
        console.log('Raw ETF data response:', JSON.stringify(data));
        
        // Check if we got valid data back
        if (!data || Object.keys(data).length === 0) {
          console.error('API returned empty data for tickers');
          setLoading(false);
          return;
        }
        
        // Store the data even if partial
        setEtfData(data);
        
        // Log what we received for each ticker
        console.log('SPY data received:', data.SPY ? 'Yes' : 'No');
        console.log('QQQ data received:', data.QQQ ? 'Yes' : 'No');
        
        // Process historical data if available
        if (data.SPY && data.SPY.historical_ratings) {
          console.log('SPY historical data available. Count:', data.SPY.historical_ratings.length);
          const spyData = data.SPY.historical_ratings.slice(-16);
          if (spyData && spyData.length) {
            const prices = spyData.map((item: any) => parseFloat(item.close));
            console.log('SPY historical prices:', prices);
            setHistoricalData(prev => ({
              ...prev,
              SPY: prices
            }));
          } else {
            console.warn('SPY historical data was empty or invalid');
          }
        } else {
          console.warn('No historical_ratings found for SPY');
        }
        
        if (data.QQQ && data.QQQ.historical_ratings) {
          console.log('QQQ historical data available. Count:', data.QQQ.historical_ratings.length);
          const qqqData = data.QQQ.historical_ratings.slice(-16);
          if (qqqData && qqqData.length) {
            const prices = qqqData.map((item: any) => parseFloat(item.close));
            console.log('QQQ historical prices:', prices);
            setHistoricalData(prev => ({
              ...prev,
              QQQ: prices
            }));
          } else {
            console.warn('QQQ historical data was empty or invalid');
          }
        } else {
          console.warn('No historical_ratings found for QQQ');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching ETF data:', error);
        // Show the error in the console with more details
        if (error instanceof Error) {
          console.error('Error details:', error.message, error.stack);
        }
        setLoading(false);
      }
    };
    
    fetchETFData();
  }, []);

  
  // Animated graph pattern for hero background
  const GraphBackground = () => (
    <Box
      sx={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        opacity: 0.07,
        zIndex: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {[...Array(20)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            height: Math.random() * 40 + 10,
            width: `${Math.random() * 40 + 10}%`,
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            borderColor: theme.palette.secondary.main,
            borderTopWidth: 2,
            borderTopStyle: 'solid',
            animation: `movePath ${Math.random() * 30 + 20}s linear infinite`,
            transformOrigin: 'left center',
            '@keyframes movePath': {
              '0%': { transform: 'translateX(-100%)' },
              '100%': { transform: 'translateX(200%)' }
            }
          }}
        />
      ))}
    </Box>
  );

interface TimeframeSignalCardProps {
  timeframe: string;
  rating: string;
  target: number;
  currentPrice: number;
}

const TimeframeSignalCard: React.FC<TimeframeSignalCardProps> = ({ 
  timeframe, 
  rating, 
  target,
  currentPrice 
}) => {
  const theme = useTheme();
  const isPositive = target > currentPrice;
  const percentChange = currentPrice > 0 ? (((target / currentPrice) - 1) * 100).toFixed(1) : '0.0';
  
  // Get proper rating color from themesV3
  const ratingColor = getRatingColor(rating);
  
  return (
    <Box sx={{ 
      flex: 1,
      minWidth: '120px',
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 1,
      overflow: 'hidden',
      height: '100%',
      borderTop: 4,
      borderColor: ratingColor // Use themesV3 color through getRatingColor
    }}>
      {/* Header with Timeframe and Rating */}
      <Box sx={{ 
        p: 1.5,
        pb: 0.5,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between'
      }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {timeframe}
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            py: 0.5,
            px: 1.5,
            borderRadius: 4,
            bgcolor: ratingColor, // Use themesV3 color through getRatingColor
            color: 'common.white',
            fontSize: '0.85rem',
            fontWeight: 600
          }}
        >
          {rating}
        </Box>
      </Box>
      
      {/* Content */}
      <Box sx={{ p: 1.5, pt: 0.5 }}>
        {/* Price Target */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Price Target
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight="bold" color="text.primary">
              ${target.toFixed(2)}
            </Typography>
          </Box>
          
          {/* Expected Return */}
          <Box 
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 1,
              fontSize: '0.875rem',
              bgcolor: theme.palette.mode === 'dark' ? 
                alpha(theme.palette.background.default, 0.4) : 
                alpha(theme.palette.background.default, 0.6),
              borderRadius: 1,
              py: 0.5,
              px: 1
            }}
          >
            <Typography 
              variant="body2" 
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.primary',
                fontWeight: 'medium'
              }}
            >
              {isPositive ? (
                <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5, color: getTrendColor(1, theme) }} />
              ) : (
                <ArrowDownwardIcon fontSize="small" sx={{ mr: 0.5, color: getTrendColor(-1, theme) }} />
              )}
              Expected: {isPositive ? '+' : ''}{percentChange}%
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

  // Add this mapper function similar to TickerDetailView and ETF pages
  const mapRatingToSignal = (rating: string | undefined): string => {
    if (!rating) return 'Hold';
    const normalized = rating.toLowerCase().trim();
    
    if (normalized.includes('strong') && normalized.includes('buy')) return 'Strong Buy';
    if (normalized === 'buy' || (normalized.includes('buy') && !normalized.includes('strong'))) return 'Buy';
    if (normalized.includes('hold') || normalized.includes('neutral')) return 'Hold';
    if (normalized === 'sell' || (normalized.includes('sell') && !normalized.includes('strong'))) return 'Sell';
    if (normalized.includes('strong') && normalized.includes('sell')) return 'Strong Sell';
    
    return 'Hold'; // Default
  };

  // Add this function to get the rating color
  const getRatingColor = (rating: string | undefined) => {
    return getSignalColor(mapRatingToSignal(rating));
  };

  return (
    <Box sx={{ overflow: 'hidden', position: 'relative' }}>
      {/* Hero Section - Simplified and honest */}
      <Box
        ref={(el: HTMLDivElement | null) => { sectionRefs.current[0] = el; }}
        sx={{
          position: 'relative',
          background: theme.palette.mode === 'dark' 
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.95)} 0%, ${alpha(theme.palette.secondary.main, 0.85)} 100%)`,
          color: 'white',
          py: { xs: 8, md: 12 },
          overflow: 'hidden',
        }}
      >
        <GraphBackground />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    sx={{ 
                      fontWeight: 800,
                      mb: 2,
                      fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                      textShadow: '0 2px 10px rgba(0,0,0,0.1)',
                      lineHeight: 1.2,
                      color: 'white'
                    }}
                  >
                    Market Intelligence for Modern Investors
                  </Typography>
                  
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 4, 
                      fontWeight: 400,
                      opacity: 0.95,
                      maxWidth: '600px',
                      lineHeight: 1.4,
                      fontSize: { xs: '1.1rem', md: '1.25rem' }
                    }}
                  >
                    Level up your investment proccess with AI-enabled insights.
                  </Typography>
                  
                  <Button 
                    variant="contained" 
                    size="large" 
                    color="secondary"
                    onClick={() => setShowRegistration(true)}
                    sx={{ 
                      py: 1.5, 
                      px: 4,
                      borderRadius: 3,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      boxShadow: `0 8px 20px ${alpha('#000', 0.25)}`,
                      background: theme.palette.mode === 'dark' 
                        ? theme.palette.secondary.main
                        : '#FFFFFF',
                      color: theme.palette.mode === 'dark' 
                        ? 'white'
                        : theme.palette.primary.main,
                      '&:hover': {
                        background: theme.palette.mode === 'dark' 
                          ? alpha(theme.palette.secondary.main, 0.9)
                          : alpha('#FFFFFF', 0.9),
                        boxShadow: `0 10px 25px ${alpha('#000', 0.3)}`,
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Start Free Today
                  </Button>
                  
                  {/* Feature highlights - simple and truthful */}
                  <Grid container spacing={2} sx={{ mt: 4 }}>
                    {[
                      { icon: <ShowChartIcon />, label: "Technical Analysis" },
                      { icon: <SecurityIcon />, label: "Market Insights" },
                      { icon: <LightbulbIcon />, label: "Investment Profiles" }
                    ].map((feature, index) => (
                      <Grid item xs={12} sm={4} key={index}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          color: 'white'
                        }}>
                          <Box sx={{ 
                            mr: 1, 
                            display: 'flex', 
                            alignItems: 'center',
                            color: theme.palette.mode === 'dark' 
                              ? theme.palette.secondary.main
                              : '#FFFFFF'
                          }}>
                            {feature.icon}
                          </Box>
                          <Typography variant="body1" fontWeight={500}>
                            {feature.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Fade>
            </Grid>
            
            {/* Simple dashboard preview - keep this visual element */}
            <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Fade in={true} timeout={1500}>
                <Box
                  sx={{
                    position: 'relative',
                    height: { xs: 300, md: 400, lg: 450 },
                    perspective: '1200px'
                  }}
                >
                  {/* Dashboard preview - simplified version */}
                  <Paper
                    elevation={8}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 20,
                      width: '90%',
                      height: '85%',
                      borderRadius: 4,
                      overflow: 'hidden',
                      transform: 'rotateY(-12deg) rotateX(5deg)',
                      transformOrigin: 'right center',
                      boxShadow: '0 30px 60px rgba(0,0,0,0.2)',
                      backgroundColor: theme.palette.background.paper,
                      zIndex: 2,
                      transition: 'all 0.4s ease-out',
                    }}
                  >
                    {/* Simple dashboard mock content */}
                    <Box sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      bgcolor: theme.palette.background.paper
                    }}>
                      <Box sx={{ 
                        height: '50px',
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        px: 2,
                        display: 'flex',
                        alignItems: 'center',
                      }}>
                        <Typography variant="subtitle1" fontWeight="bold">Market Dashboard</Typography>
                      </Box>
                      
                      <Box sx={{ pl: 1.5, pr: 1.5, flex: 1 }}>
                        {/* Simple placeholder chart */}
                        <Box sx={{ mb: 3, bgcolor: alpha(theme.palette.divider, 0.1), borderRadius: 2 }} />
                        {/*Add image from the local folder "SMADashboardImage.jpg" */}
                        <CardMedia
                          component="img"
                          image='/SMADashboardImage.jpg'
                          alt="Dashboard Preview"
                          sx={{ height: 750, borderRadius: 2, mb: 3 }}
                        
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Keep the scroll to top button separately if needed */}
      <Zoom in={showScrollTop}>
        <Fab 
          color="primary" 
          size="small" 
          onClick={scrollToTop}
          sx={{ 
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            zIndex: 10,
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>

      {/* First transition - After Hero, before Features */}
      <Box 
        sx={{ 
          py: 3, 
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.primary.dark, 0.2) : alpha(theme.palette.primary.light, 0.1),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <Container>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500, textAlign: 'center' }}>
              Discover our powerful investment intelligence toolkit
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              ml: 2,
              color: theme.palette.primary.main 
            }}>
              <ArrowDownwardIcon fontSize="small" />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Feature Overview Section - Converted to tabs for compactness */}
      <Box 
        ref={(el: HTMLDivElement | null) => { sectionRefs.current[1] = el; }}
        sx={{ py: 8, bgcolor: theme.palette.mode === 'dark' ? 'background.default' : '#ffffff', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box 
              sx={{ 
                width: 60, 
                height: 4, 
                bgcolor: theme.palette.primary.main, 
                borderRadius: 2,
                mb: 1
              }} 
            />
          </Box>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={10} textAlign="center" mb={5}>
              <Typography 
                variant="h3" 
                component="h2" 
                fontWeight="bold"
                sx={{ mb: 2 }}
              >
                The Smart Investor's Competitive Edge
              </Typography>
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ 
                  maxWidth: 700, 
                  mx: 'auto' 
                }}
              >
                Our comprehensive tools work together to transform market data into actionable intelligence.
              </Typography>
            </Grid>
          </Grid>

          {/* Feature Cards */}
          <Grid container spacing={4} justifyContent="center" sx={{ mt: 2 }}>
            {/* Personalized Profiles */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={1}
                sx={{ 
                  height: '100%', 
                  borderRadius: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 8
                  }
                }}
              >
                <CardContent sx={{ p: 2, pb: 2, flexGrow: 1 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <PersonIcon color="primary" fontSize="medium" />
                  </Box>
                  
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Personalized Profiles
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Create customized investment profiles based on your goals, risk tolerance, and time horizon for better investing outcomes.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Market Intelligence */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={1}
                sx={{ 
                  height: '100%', 
                  borderRadius: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 8
                  }
                }}
              >
                <CardContent sx={{ p: 2, pb: 2, flexGrow: 1 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.secondary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <ShowChartIcon color="secondary" fontSize="medium" />
                  </Box>
                  
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Market Intelligence
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Access real-time market data, trends, and signals to make data-driven decisions with greater confidence.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Investment Tools */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={1}
                sx={{ 
                  height: '100%', 
                  borderRadius: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 8
                  }
                }}
              >
                <CardContent sx={{ p: 2, pb: 2, flexGrow: 1 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <BuildIcon color="success" fontSize="medium" />
                  </Box>
                  
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Investment Tools
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Leverage advanced portfolio analysis tools, watchlists, and risk metrics for optimal asset allocation.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Deep Analysis */}
            <Grid item xs={12} sm={6} md={3}>
              <Card 
                elevation={1}
                sx={{ 
                  height: '100%', 
                  borderRadius: 3,
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 8
                  }
                }}
              >
                <CardContent sx={{ p: 2, pb: 2, flexGrow: 1 }}>
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      bgcolor: alpha(theme.palette.warning.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <QueryStatsIcon color="warning" fontSize="medium" />
                  </Box>
                  
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Deep Analysis
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Unlock comprehensive analytical capabilities with AI-powered insights and pattern recognition.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Second transition - After Features, before Market Analysis */}
      <Box sx={{ 
        py: 4, 
        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.4) : alpha(theme.palette.primary.light, 0.05),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
      }}>
        <Container>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
              From data to decisions: Our analytics in action
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <ArrowDownwardIcon color="primary" />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Market Preview Section - Using real ETF data */}
      <Box 
        ref={(el: HTMLDivElement | null) => { sectionRefs.current[2] = el; }}
        id="market-snapshot" 
        sx={{ py: 7, bgcolor: 'background.default' }}
      >
        <Container>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                fontWeight: 700, 
                mb: 2 
              }}
            >
              Market ETF Analysis
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto' 
              }}
            >
              Track market performance and receive AI-powered insights for informed investment decisions.
            </Typography>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress color="secondary" />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 4,
              }}
            >
              {/* SPY ETF Card */}
              <Box sx={{ minHeight: 480 }}>
                <Paper
                  elevation={theme.palette.mode === 'dark' ? 4 : 1}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      transform: 'translateY(-4px)',
                      boxShadow: 6 
                    },
                  }}
                >
                  {/* Header with Ticker and Price */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    p: 3,
                    borderBottom: 1,
                    borderColor: 'divider'
                  }}>
                    <Typography variant="h4" fontWeight={700} color={baseColors.blue}>SPY</Typography>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Current Price</Typography>
                      <Typography variant="h5" color={baseColors.blue} fontWeight="bold">
                        ${etfData.SPY?.close?.toFixed(2) || '450.00'}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Interactive Chart */}
                  <Box sx={{ p: 3, position: 'relative' }}>
                    {etfData.SPY?.historical_ratings ? (
                      <StockChart 
                        data={etfData.SPY.historical_ratings} 
                        height={220} 
                        isTrimmed={true} 
                      />
                    ) : (
                      <Box sx={{ 
                        height: 220, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                        borderRadius: 2
                      }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <CircularProgress size={30} sx={{ mb: 2 }} />
                          <Typography variant="body2" color="text.secondary">
                            Chart data loading...
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    {/* Premium Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 20,
                        right: 20,
                        bgcolor: alpha(theme.palette.background.paper, 0.8),
                        backdropFilter: 'blur(5px)',
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        zIndex: 2
                      }}
                    >
                      <LockIcon fontSize="small" color="primary" />
                      <Typography variant="caption" fontWeight="medium">Premium Data</Typography>
                    </Box>
                  </Box>
                  
                  {/* Signals and Targets */}
                  <Box sx={{ px: 3, mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>Signals & Price Targets</Typography>
                    {etfData.SPY && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <TimeframeSignalCard
                          timeframe="Short Term"
                          rating={etfData.SPY?.ShortTermRating?.toUpperCase() || 'N/A'} 
                          target={etfData.SPY?.ShortTermPriceTarget || 0}
                          currentPrice={etfData.SPY?.close || 0}
                        />
                        <TimeframeSignalCard
                          timeframe="Mid Term"
                          rating={etfData.SPY?.SwingTradeRating?.toUpperCase() || 'N/A'} 
                          target={etfData.SPY?.SwingTradePriceTarget || 0}
                          currentPrice={etfData.SPY?.close || 0}
                        />
                        <TimeframeSignalCard
                          timeframe="Long Term"
                          rating={etfData.SPY?.LongTermRating?.toUpperCase() || 'N/A'} 
                          target={etfData.SPY?.LongTermPriceTarget || 0}
                          currentPrice={etfData.SPY?.close || 0}
                        />
                      </Box>
                    )}
                  </Box>
                  
                  {/* Analysis Summary */}
                  <Box sx={{ px: 3, pb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      AI Analysis Summary
                    </Typography>
                    <Typography variant="body2">
                      {etfData.SPY?.Summary ? 
                        (typeof etfData.SPY.Summary === 'string' && etfData.SPY.Summary.startsWith('{') ? 
                          (() => {
                            try {
                              return JSON.parse(etfData.SPY.Summary).description;
                            } catch (e) {
                              return etfData.SPY.Summary;
                            }
                          })() 
                          : etfData.SPY.Summary.replace(/"/g, ''))
                        : 'SAMPLE: The S&P 500 ETF shows mixed signals across different time horizons. Technical indicators suggest potential for continued upward momentum, though with increasing volatility expected. Market sentiment remains cautiously optimistic, with strong corporate earnings supporting the bullish case despite concerns about inflation and interest rates. Investors should consider their time horizon carefully when establishing positions.'}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
              
              {/* QQQ ETF Card - Similar structure with different data */}
              <Box sx={{ minHeight: 480 }}>
                <Paper
                  elevation={theme.palette.mode === 'dark' ? 4 : 1}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      transform: 'translateY(-4px)',
                      boxShadow: 6 
                    },
                  }}
                >
                  {/* Header with Ticker and Price */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    p: 3,
                    borderBottom: 1,
                    borderColor: 'divider'
                  }}>
                    <Typography variant="h4" fontWeight={700} color={baseColors.blue}>QQQ</Typography>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Current Price</Typography>
                      <Typography variant="h5" color={baseColors.blue} fontWeight="bold">
                        ${etfData.QQQ?.close?.toFixed(2) || '350.00'}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Interactive Chart */}
                  <Box sx={{ p: 3, position: 'relative' }}>
                    {etfData.QQQ?.historical_ratings ? (
                      <StockChart 
                        data={etfData.QQQ.historical_ratings} 
                        height={220} 
                        isTrimmed={true} 
                      />
                    ) : (
                      <Box sx={{ 
                        height: 220, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: alpha(theme.palette.background.default, 0.5),
                        borderRadius: 2
                      }}>
                        <Box sx={{ textAlign: 'center' }}>
                          <CircularProgress size={30} sx={{ mb: 2 }} />
                          <Typography variant="body2" color="text.secondary">
                            Chart data loading...
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    
                    {/* Premium Overlay */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 20,
                        right: 20,
                        bgcolor: alpha(theme.palette.background.paper, 0.8),
                        backdropFilter: 'blur(5px)',
                        borderRadius: 2,
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        zIndex: 2
                      }}
                    >
                      <LockIcon fontSize="small" color="primary" />
                      <Typography variant="caption" fontWeight="medium">Premium Data</Typography>
                    </Box>
                  </Box>
                  
                  {/* Signals and Targets */}
                  <Box sx={{ px: 3, mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>Signals & Price Targets</Typography>
                    {etfData.QQQ && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <TimeframeSignalCard
                          timeframe="Short Term"
                          rating={etfData.QQQ?.ShortTermRating?.toUpperCase() || 'N/A'} 
                          target={etfData.QQQ?.ShortTermPriceTarget || 0}
                          currentPrice={etfData.QQQ?.close || 0}
                        />
                        <TimeframeSignalCard
                          timeframe="Mid Term"
                          rating={etfData.QQQ?.SwingTradeRating?.toUpperCase() || 'N/A'} 
                          target={etfData.QQQ?.SwingTradePriceTarget || 0}
                          currentPrice={etfData.QQQ?.close || 0}
                        />
                        <TimeframeSignalCard
                          timeframe="Long Term"
                          rating={etfData.QQQ?.LongTermRating?.toUpperCase() || 'N/A'} 
                          target={etfData.QQQ?.LongTermPriceTarget || 0}
                          currentPrice={etfData.QQQ?.close || 0}
                        />
                      </Box>
                    )}
                  </Box>
                  
                  {/* Analysis Summary */}
                  <Box sx={{ px: 3, pb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      AI Analysis Summary
                    </Typography>
                    <Typography variant="body2">
                      {etfData.QQQ?.Summary ? 
                        (typeof etfData.QQQ.Summary === 'string' && etfData.QQQ.Summary.startsWith('{') ? 
                          (() => {
                            try {
                              return JSON.parse(etfData.QQQ.Summary).description;
                            } catch (e) {
                              return etfData.QQQ.Summary;
                            }
                          })() 
                          : etfData.QQQ.Summary.replace(/"/g, ''))
                        : 'SAMPLE: QQQ has shown strong performance recently, entering a bull market with significant gains driven by favorable economic news and strong sector performance. However, volatility is expected short-term.'}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}
        </Container>
      </Box>

      {/* Third transition - After Market Analysis, before Investment Profiles */}
      <Box sx={{ 
        py: 4, 
        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.4) : alpha(theme.palette.primary.light, 0.05),
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
      }}>
        <Container>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
              Apply market insights to your personal investment style
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <ArrowDownwardIcon color="primary" />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Investment Profile Types - Interactive Risk Spectrum */}
      <Box 
        ref={(el: HTMLDivElement | null) => { sectionRefs.current[3] = el; }}
        sx={{ 
          py: 8, 
          pb: 16, // Extra padding at bottom for cards
          bgcolor: theme.palette.mode === 'dark' ? '#111722' : '#f8f9fd',
          borderRadius: { md: 3 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background decoration elements */}
        <Box sx={{ 
          position: 'absolute', 
          top: -100, 
          right: -100, 
          width: 300, 
          height: 300, 
          borderRadius: '50%', 
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
          zIndex: 0
        }} />
        
        <Box sx={{ 
          position: 'absolute', 
          bottom: -150, 
          left: -150, 
          width: 400, 
          height: 400, 
          borderRadius: '50%', 
          background: `radial-gradient(circle, ${alpha(theme.palette.success.main, 0.08)}, transparent)`,
          zIndex: 0
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box 
              sx={{ 
                width: 60, 
                height: 4, 
                bgcolor: theme.palette.primary.main, 
                borderRadius: 2,
                mb: 1
              }} 
            />
          </Box>
          <Typography 
            variant="h3" 
            component="h2" 
            fontWeight="bold"
            sx={{ mb: 2, textAlign: 'center' }}
          >
            Investment Personality Profiles
          </Typography>
          
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              maxWidth: 700, 
              mx: 'auto',
              textAlign: 'center',
              mb: 4
            }}
          >
            Discover which investment approach aligns with your goals, risk tolerance, and personality.
          </Typography>

          {/* Interactive Risk Tolerance Spectrum */}
          <Box sx={{ 
            mb: 2,
            mx: 'auto', 
            maxWidth: { xs: '95%', sm: '85%', md: 1100, lg: 1200 },
            position: 'relative', 
            px: { xs: 3, md: 2 }
          }}>
            {/* Risk spectrum bar */}
            <Box sx={{ 
              height: 10, 
              bgcolor: alpha(theme.palette.divider, 0.3), 
              width: '100%', 
              position: 'relative',
              borderRadius: 5,
              overflow: 'hidden',
              mb: 0
            }}>
              {/* Gradient overlay for the slider bar */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '100%',
                background: `linear-gradient(to right, 
                  ${theme.palette.success.main}, 
                  ${theme.palette.info.light},
                  ${theme.palette.info.main}, 
                  ${theme.palette.warning.main}, 
                  ${theme.palette.error.main})`,
                borderRadius: 5
              }} />
            </Box>
          </Box>

          {/* Profile Dots - extremely compact spacing */}
          <Box sx={{ 
            position: 'relative', 
            height: 120,
            mt: 0.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
          }}>
            {[
              {
                name: "Quality Income Investor",
                position: 10,
                color: theme.palette.success.main,
                riskLevel: "Low - Moderate",
                returnPotential: "Low - Moderate",
                description: "Targets companies with stable cash flows that regularly distribute earnings to shareholders through dividends."
              },
              {
                name: "Balanced Growth Investor",
                position: 30,
                color: theme.palette.success.main,
                riskLevel: "Moderate",
                returnPotential: "Moderate",
                description: "Combines elements of multiple strategies to create a diversified approach to investing across different sectors and styles."
              },
              {
                name: "Value Investor",
                position: 50,
                color: theme.palette.info.main,
                riskLevel: "Moderate - High",
                returnPotential: "Moderate - High",
                description: "Seeks undervalued companies trading below their intrinsic value, often with low P/E ratios and higher dividend yields."
              },
              {
                name: "Aggressive Growth Investor",
                position: 70,
                color: theme.palette.warning.main,
                riskLevel: "High",
                returnPotential: "High",
                description: "Focuses on companies with high growth potential that are expected to grow sales and earnings faster than industry or market average."
              },
              {
                name: "Ultra-Aggressive Active Investor",
                position: 90,
                color: theme.palette.error.main,
                riskLevel: "High",
                returnPotential: "Very High",
                description: "Focuses on stocks expected to give big returns, buying rising stocks and avoiding falling ones."
              }
            ].map((profile, index) => {
              const isActive = activeProfileIndex === index;
              
              return (
                <Box 
                  key={index}
                  sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '20%',
                    position: 'relative'
                  }}
                >
                  {/* Wrap both dot and text in a hover-sensitive container */}
                  <Box 
                    sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.5,
                      cursor: 'pointer'
                    }}
                    onMouseEnter={() => setHoveredProfileIndex(index)}
                    onMouseLeave={() => setHoveredProfileIndex(null)}
                  >
                    {/* Profile dot - removed mouse handlers from here */}
                    <Box 
                      sx={{
                        width: hoveredProfileIndex === index ? 36 : 30,
                        height: hoveredProfileIndex === index ? 36 : 30,
                        borderRadius: '50%',
                        bgcolor: profile.color,
                        border: '3px solid white',
                        boxShadow: `0 0 12px ${alpha(profile.color, 0.6)}`,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.1)'
                        }
                      }}
                    />
                    
                    {/* Profile name - now part of the hover area */}
                    <Typography 
                      variant="subtitle1"
                      fontWeight="medium"
                      sx={{ 
                        color: profile.color,
                        textAlign: 'center',
                        textShadow: theme.palette.mode === 'dark' ? 
                          '0 0 6px rgba(0,0,0,0.5)' : 
                          '0 0 8px rgba(255,255,255,0.8)'
                      }}
                    >
                      {profile.name}
                    </Typography>
                  </Box>
                  
                  {/* Profile metrics - transparent background */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 0.5,
                    mt: 0.3,
                    width: '100%',
                    justifyContent: 'center',
                    borderRadius: 1.5,
                    py: 0.5,
                    px: 1.2,
                  }}>
                    {/* Risk Level */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: profile.color,
                          fontSize: '0.75rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {profile.riskLevel}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          ml: 0.5
                        }}
                      >
                        Risk
                      </Typography>
                    </Box>
                    
                    {/* Return Potential */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexWrap: 'nowrap',
                      whiteSpace: 'nowrap'
                    }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.75rem',
                          fontWeight: 'medium',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {profile.returnPotential}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: theme.palette.text.secondary,
                          fontSize: '0.75rem',
                          ml: 0.5,
                          flexShrink: 0
                        }}
                      >
                        Return
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Floating card on hover - without repetitive title */}
                  {hoveredProfileIndex === index && (
                    <Paper
                      elevation={8}
                      sx={{
                        position: 'absolute',
                        top: 90,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 320,
                        maxWidth: 'calc(100vw - 48px)',
                        zIndex: 20,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: alpha(profile.color, 0.3),
                        boxShadow: `0 10px 20px ${alpha(profile.color, 0.3)}`
                      }}
                    >
                      {/* Risk indicator bar at top */}
                      <Box 
                        sx={{ 
                          height: 5, 
                          width: '100%', 
                          bgcolor: alpha(theme.palette.divider, 0.2),
                          position: 'relative',
                        }}
                      >
                        <Box sx={{ 
                          position: 'absolute', 
                          left: 0, 
                          top: 0, 
                          height: '100%', 
                          width: `${profile.position}%`,
                          bgcolor: profile.color 
                        }} />
                      </Box>
                      
                      {/* Just show the description - removed the title section */}
                      <Box sx={{ p: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {profile.description}
                        </Typography>
                      </Box>
                    </Paper>
                  )}
                </Box>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* Fourth transition - After Investment Profiles, before Pricing */}
      <Box sx={{ 
        py: 4, 
        bgcolor: 'background.default',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`
      }}>
        <Container>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500 }}>
              Start your journey to smarter investing today
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <ArrowDownwardIcon color="primary" />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Subscription Plans with themed background */}
      <Box 
        ref={(el: HTMLDivElement | null) => { sectionRefs.current[4] = el; }}
        sx={{ 
          py: 8, 
          bgcolor: semanticColors.bannerBg || baseColors.blue,
          color: baseColors.white,
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <Box 
              sx={{ 
                width: 60, 
                height: 4, 
                bgcolor: baseColors.white,
                borderRadius: 2,
                mb: 1
              }} 
            />
          </Box>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={10} textAlign="center" mb={5}>
              <Typography 
                variant="h3" 
                component="h2" 
                fontWeight="bold"
                sx={{ mb: 2, color: baseColors.white }}
              >
                Choose Your Investment Path
              </Typography>
              <Typography 
                variant="body1"
                sx={{ 
                  maxWidth: 700, 
                  mx: 'auto',
                  color: alpha(baseColors.white, 0.9)
                }}
              >
                Select the plan that gives you the right tools for your investment strategy.
              </Typography>
            </Grid>
          </Grid>

          {/* Use the SubscriptionPlans component here */}
          <SubscriptionPlans 
            onSelect={() => setShowRegistration(true)}
            showRegisterButton={true}
            containerBackgroundColor={semanticColors.bannerBg || baseColors.blue}
            customButtonText={(role) => {
              switch(role) {
                case 'free': return 'Start Free';
                case 'base': return 'Get Base';
                case 'premium': return 'Get Premium';
                default: return 'Select Plan';
              }
            }}
          />
        </Container>
      </Box>
    </Box>
  );
};

export default UnregisteredContent; 